import {
  useGetBlobQuery,
  useGetBlobReadUrlQuery,
  useGetFileReadUrlQuery,
  useProjectId,
} from '@consigli/hooks';

import { isPDFDocumentType } from '@/util/document-check';

export interface DocumentSourceUrl {
  readUrl: string | undefined;
  isLoading: boolean;
  hasError: boolean;
  isUnconvertible: boolean;
}

export const useDocumentSourceUrl = (
  packageId: string | null | undefined,
  blobId: string | null | undefined,
): DocumentSourceUrl => {
  const projectId = useProjectId();
  const skipBlob = !blobId || !packageId;

  const { data: blob, isLoading: isLoadingBlob } = useGetBlobQuery(
    { projectId, packageId: packageId ?? '', blobId: blobId ?? '' },
    { skip: skipBlob },
  );
  const convertedPdfFileId = blob?.convertedPdfFileId ?? null;

  const {
    data: blobUrl,
    isLoading: isLoadingBlobUrl,
    isError: isBlobUrlError,
  } = useGetBlobReadUrlQuery(
    { projectId, packageId: packageId ?? '', blobId: blobId ?? '' },
    { skip: skipBlob || isLoadingBlob || !!convertedPdfFileId },
  );
  const {
    data: convertedPdfUrl,
    isLoading: isLoadingConvertedPdfUrl,
    isError: isConvertedPdfUrlError,
  } = useGetFileReadUrlQuery(
    { projectId, fileId: convertedPdfFileId ?? '' },
    { skip: skipBlob || !convertedPdfFileId },
  );

  const isLoading = !skipBlob && (isLoadingBlob || isLoadingBlobUrl || isLoadingConvertedPdfUrl);
  const isUnconvertible =
    !isLoading && !!blob && !convertedPdfFileId && !isPDFDocumentType(blob.name);
  const readUrl = convertedPdfFileId ? convertedPdfUrl?.readUrl : blobUrl?.readUrl;

  return {
    readUrl: skipBlob || isLoading || isUnconvertible ? undefined : readUrl,
    isLoading,
    hasError: !skipBlob && !isLoading && (isBlobUrlError || isConvertedPdfUrlError || !readUrl),
    isUnconvertible,
  };
};
